import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { submissionsAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    Send, 
    CheckCircle2,
    UserPlus,
    Shield,
    Handshake,
    Info
} from 'lucide-react';

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming", "National"
];

const STAKEHOLDER_TYPES = [
    "Employer",
    "Service/Support Provider",
    "College or Training Provider",
    "High School",
    "Transportation",
    "Government",
    "Other"
];

const NominatePartnerPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, login } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        // Organization Info
        name: '',
        mission: '',
        website: '',
        state: '',
        city: '',
        stakeholder_types: [],
        
        // Organization Contact
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        
        // Nomination Details
        relationship_description: '',
        why_trusted: '',
        collaboration_history: '',
        
        // Additional Info
        other_info: '',
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Organization name is required';
        if (!formData.mission.trim()) newErrors.mission = 'Mission statement is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.stakeholder_types.length) newErrors.stakeholder_types = 'Please select at least one organization type';
        if (!formData.contact_name.trim()) newErrors.contact_name = 'Contact name is required';
        if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
        if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
            newErrors.contact_email = 'Invalid email format';
        }
        if (!formData.relationship_description.trim()) newErrors.relationship_description = 'Please describe your relationship';
        if (!formData.why_trusted.trim()) newErrors.why_trusted = 'Please explain why this is a trusted partner';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) return;
        
        setSubmitting(true);
        
        try {
            // Add submission type and nominator info
            const submissionData = {
                ...formData,
                submission_type: 'trusted_partner_nomination',
                requester_name: user?.name || 'Member',
                requester_email: user?.email || '',
                requester_role: 'HAAPE Member',
                nominated_by_member: true,
            };
            
            await submissionsAPI.create(submissionData);
            setSubmitted(true);
            toast.success('Your partner nomination has been submitted!');
        } catch (error) {
            console.error('Submission error:', error);
            toast.error('Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Login required
    if (!isAuthenticated) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="login-required">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <Shield className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle>Member Access Required</CardTitle>
                        <CardDescription>
                            Please log in to nominate a trusted partner for HAAPE membership.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={login} className="w-full" data-testid="login-btn">
                            Member Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="submission-success">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="h-8 w-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold mb-2">Nomination Submitted!</h2>
                        <p className="text-muted-foreground mb-6">
                            Thank you for nominating a trusted partner. Our team will review this nomination 
                            and reach out to the organization to invite them to join HAAPE.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <Button onClick={() => navigate('/directory')} data-testid="go-directory-btn">
                                View Public Directory
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => { setSubmitted(false); setFormData({
                                    name: '', mission: '', website: '', state: '', city: '', stakeholder_type: '',
                                    contact_name: '', contact_email: '', contact_phone: '',
                                    relationship_description: '', why_trusted: '', collaboration_history: '', other_info: ''
                                }); }}
                                data-testid="nominate-another-btn"
                            >
                                Nominate Another
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="nominate-partner-page">
            <div className="container max-w-2xl mx-auto py-8 px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                        <Handshake className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">Nominate a HAAPE Member</h1>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                        Recommend a vetted, trusted organization you know and have worked with to become a HAAPE member.
                        They will receive login access to the Hub and all member resources.
                    </p>
                </div>

                <Alert className="mb-6 bg-primary/5 border-primary/20">
                    <Shield className="h-4 w-4 text-primary" />
                    <AlertDescription>
                        <strong>This is for trusted partners only.</strong> Nominated organizations will be invited to join HAAPE, 
                        sign our Terms & Conditions, and gain full member access including login credentials, the member directory, 
                        and the ability to contribute to our ecosystem. Only nominate organizations you personally know and trust.
                    </AlertDescription>
                </Alert>

                <form onSubmit={handleSubmit}>
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg">Organization Information</CardTitle>
                            <CardDescription>Tell us about the organization you're nominating</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Organization Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="e.g., Aspiritech"
                                    data-testid="input-name"
                                />
                                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="mission">Their Mission *</Label>
                                <Textarea
                                    id="mission"
                                    value={formData.mission}
                                    onChange={(e) => handleChange('mission', e.target.value)}
                                    placeholder="What is this organization's mission?"
                                    rows={3}
                                    data-testid="input-mission"
                                />
                                {errors.mission && <p className="text-sm text-destructive">{errors.mission}</p>}
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Organization Type(s) * <span className="text-sm font-normal text-muted-foreground">— select all that apply</span></Label>
                                    <div className="space-y-2">
                                        {STAKEHOLDER_TYPES.map((type) => {
                                            const isChecked = formData.stakeholder_types.includes(type);
                                            return (
                                                <Label
                                                    key={type}
                                                    htmlFor={`nom-type-${type}`}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                                        isChecked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
                                                    }`}
                                                >
                                                    <Checkbox
                                                        id={`nom-type-${type}`}
                                                        checked={isChecked}
                                                        onCheckedChange={(checked) => {
                                                            const updated = checked
                                                                ? [...formData.stakeholder_types, type]
                                                                : formData.stakeholder_types.filter(t => t !== type);
                                                            handleChange('stakeholder_types', updated);
                                                        }}
                                                    />
                                                    <span className="text-sm font-medium">{type}</span>
                                                </Label>
                                            );
                                        })}
                                    </div>
                                    {errors.stakeholder_types && <p className="text-sm text-destructive">{errors.stakeholder_types}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="website">Website</Label>
                                    <Input
                                        id="website"
                                        value={formData.website}
                                        onChange={(e) => handleChange('website', e.target.value)}
                                        placeholder="https://example.org"
                                        data-testid="input-website"
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="state">State *</Label>
                                    <Select 
                                        value={formData.state} 
                                        onValueChange={(v) => handleChange('state', v)}
                                    >
                                        <SelectTrigger data-testid="select-state">
                                            <SelectValue placeholder="Select state" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {US_STATES.map((state) => (
                                                <SelectItem key={state} value={state}>{state}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.state && <p className="text-sm text-destructive">{errors.state}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="city">City</Label>
                                    <Input
                                        id="city"
                                        value={formData.city}
                                        onChange={(e) => handleChange('city', e.target.value)}
                                        placeholder="e.g., Chicago"
                                        data-testid="input-city"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg">Organization Contact</CardTitle>
                            <CardDescription>Who should we reach out to?</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="contact_name">Contact Name *</Label>
                                    <Input
                                        id="contact_name"
                                        value={formData.contact_name}
                                        onChange={(e) => handleChange('contact_name', e.target.value)}
                                        placeholder="Primary contact's name"
                                        data-testid="input-contact-name"
                                    />
                                    {errors.contact_name && <p className="text-sm text-destructive">{errors.contact_name}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contact_email">Contact Email *</Label>
                                    <Input
                                        id="contact_email"
                                        type="email"
                                        value={formData.contact_email}
                                        onChange={(e) => handleChange('contact_email', e.target.value)}
                                        placeholder="contact@example.org"
                                        data-testid="input-contact-email"
                                    />
                                    {errors.contact_email && <p className="text-sm text-destructive">{errors.contact_email}</p>}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact_phone">Contact Phone</Label>
                                <Input
                                    id="contact_phone"
                                    value={formData.contact_phone}
                                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                                    placeholder="(555) 123-4567"
                                    data-testid="input-contact-phone"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Shield className="h-5 w-5 text-primary" />
                                Your Recommendation
                            </CardTitle>
                            <CardDescription>Help us understand why this is a trusted partner</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Alert>
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                    As a HAAPE member, your nomination is highly valued. Organizations you recommend 
                                    will receive priority consideration for membership.
                                </AlertDescription>
                            </Alert>

                            <div className="space-y-2">
                                <Label htmlFor="relationship_description">How do you know this organization? *</Label>
                                <Textarea
                                    id="relationship_description"
                                    value={formData.relationship_description}
                                    onChange={(e) => handleChange('relationship_description', e.target.value)}
                                    placeholder="Describe your relationship with this organization..."
                                    rows={3}
                                    data-testid="input-relationship"
                                />
                                {errors.relationship_description && <p className="text-sm text-destructive">{errors.relationship_description}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="why_trusted">Why do you consider them a trusted partner? *</Label>
                                <Textarea
                                    id="why_trusted"
                                    value={formData.why_trusted}
                                    onChange={(e) => handleChange('why_trusted', e.target.value)}
                                    placeholder="What makes this organization aligned with HAAPE's mission?"
                                    rows={3}
                                    data-testid="input-why-trusted"
                                />
                                {errors.why_trusted && <p className="text-sm text-destructive">{errors.why_trusted}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="collaboration_history">Any collaboration history?</Label>
                                <Textarea
                                    id="collaboration_history"
                                    value={formData.collaboration_history}
                                    onChange={(e) => handleChange('collaboration_history', e.target.value)}
                                    placeholder="Have you worked together on projects, events, or initiatives?"
                                    rows={2}
                                    data-testid="input-collaboration"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="other_info">Any other information?</Label>
                                <Textarea
                                    id="other_info"
                                    value={formData.other_info}
                                    onChange={(e) => handleChange('other_info', e.target.value)}
                                    placeholder="Anything else that would help us evaluate this nomination..."
                                    rows={2}
                                    data-testid="input-other"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex justify-end">
                        <Button type="submit" disabled={submitting} className="gap-2" data-testid="submit-btn">
                            {submitting ? (
                                <>Submitting...</>
                            ) : (
                                <>
                                    Submit Nomination
                                    <Send className="h-4 w-4" />
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NominatePartnerPage;
